import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { api } from 'constants/constant';
import { getHeaders, getUserDetails } from 'auth/auth';

/**
 * V2 User Management API Service
 * Connects to the new backend V2 user management endpoints
 * Replaces legacy admin employee and consumer APIs
 */
export const userManagementV2API = createApi({
  reducerPath: 'userManagementV2',
  tagTypes: [
    'Users',
    'UserDetails',
    'Invitations',
    'UserRoles',
    'UserBranches',
    'InvitationAnalytics',
    'UserNMLS',
    'VerifiedUsers'
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: api,
    credentials: 'include', // CRITICAL: Include cookies in cross-origin requests
  }),
  endpoints: (builder) => ({

    // =============================================================================
    // USER MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * List all users in the organization
     * GET /api/v1/b2b/users
     */
    listUsers: builder.query({
      query: ({ skip = 0, limit = 100, role, roles, branch, is_active, search }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });

        if (role) params.append('role', role);
        if (Array.isArray(roles)) {
          roles
            .filter((roleValue) => roleValue && String(roleValue).trim())
            .forEach((roleValue) => {
              params.append('role_in', String(roleValue).trim());
            });
        }
        if (branch) params.append('branch', branch);
        if (is_active !== undefined) params.append('is_active', is_active.toString());
        if (search) params.append('search', search);

        return {
          url: `/b2b/users/?${params.toString()}`,  // Added trailing slash to avoid 307 redirect
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Users'],
    }),

    /**
     * List consumers (borrowers) with backend-scoped visibility
     * GET /api/v1/b2b/users/consumers-list
     *
     * Uses the dedicated consumers-list endpoint which applies
     * role_filter="consumer" and visibility scoping on the backend.
     */
    listConsumers: builder.query({
      query: ({ skip = 0, limit = 100, search } = {}) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });

        if (search) {
          params.append('search', search);
        }

        return {
          url: `/b2b/users/consumers-list?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Users'],
    }),

    /**
     * Fetch only NMLS-verified users for manager assignment dropdowns
     * GET /api/v1/users?nmls_verified=true
     */
    listNmlsVerifiedUsers: builder.query({
      query: ({ search, roles = ['branch_admin'], limit = 50 } = {}) => {
        const params = new URLSearchParams({
          skip: '0',
          limit: limit.toString(),
          nmls_verified: 'true',
        });

        roles
          .filter(Boolean)
          .forEach((role) => {
            const normalizedRole = String(role).trim();
            if (normalizedRole) {
              params.append('role_in', normalizedRole);
            }
          });

        if (search) {
          params.append('q', search);
        }

        return {
          url: `/b2b/users/?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['VerifiedUsers'],
    }),

    /**
     * Get user details by ID
     * GET /api/v1/users/{user_id}
     */
    getUserDetails: builder.query({
      query: (userId) => ({
        url: `/b2b/users/${userId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, userId) => [
        { type: 'UserDetails', id: userId }
      ],
    }),

    /**
     * Invite a new user to the organization
     * POST /api/v1/users/invite
     *
     * NOTE: This endpoint expects the payload to be wrapped
     * in a top-level "body" key due to how the FastAPI route
     * is defined, so we send `{ body: inviteData }` here.
     */
    inviteUser: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite',
        method: 'POST',
        headers: getHeaders(),
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    /**
     * Invite a consumer from the loan officer flow
     * POST /api/v1/b2b/users/invite-user-officer
     */
    inviteUserOfficer: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite-user-officer',
        method: 'POST',
        headers: getHeaders(),
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    /**
     * Invite a new employee to the organization
     * POST /api/v1/users/invite-employee
     * Dedicated endpoint for employee invitations with better error handling
     */
    inviteEmployee: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite-employee',
        method: 'POST',
        headers: getHeaders(),
        // Wrap to satisfy FastAPI body parser that sometimes expects { body: ... }
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    /**
     * Update user role
     * PUT /api/v1/users/{user_id}/role
     */
    updateUserRole: builder.mutation({
      query: ({ userId, role }) => ({
        url: `/b2b/users/${userId}/role`,
        method: 'PUT',
        headers: getHeaders(),
        body: { role },
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
        'UserRoles'
      ],
    }),

    /**
     * Remove user from organization
     * DELETE /api/v1/users/{user_id}
     */
    removeUser: builder.mutation({
      query: (userId) => ({
        url: `/b2b/users/${userId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: ['Users'],
    }),

    // =============================================================================
    // INVITATION MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * Create a new invitation
     * POST /api/v1/users/invitations/create
     */
    createInvitation: builder.mutation({
      query: (invitationData) => ({
        url: '/b2b/users/invitations/create',
        method: 'POST',
        headers: getHeaders(),
        body: invitationData,
      }),
      invalidatesTags: ['Invitations'],
    }),

    /**
     * List invitations for the organization
     * GET /api/v1/users/invitations
     */
    listInvitations: builder.query({
      query: ({ status_filter, skip = 0, limit = 100, search }) => {
        const params = new URLSearchParams({
          skip: skip.toString(),
          limit: limit.toString(),
        });

        if (status_filter) params.append('status_filter', status_filter);
        if (search) params.append('search', search);

        return {
          url: `/b2b/users/invitations?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Invitations'],
    }),

    /**
     * Revoke an invitation
     * DELETE /api/v1/users/invitations/{invitation_code}
     */
    revokeInvitation: builder.mutation({
      query: (invitationCode) => ({
        url: `/b2b/users/invitations/${invitationCode}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: ['Invitations'],
    }),

    /**
     * Resend an invitation email
     * POST /api/v1/users/invitations/{invitation_code}/resend
     */
    resendInvitation: builder.mutation({
      query: (invitationCode) => ({
        url: `/b2b/users/invitations/${invitationCode}/resend`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: ['Invitations'],
    }),

    // =============================================================================
    // USER PROFILE & SETTINGS ENDPOINTS
    // =============================================================================

    /**
     * Update user's employment type
     * PUT /api/v1/users/{user_id}/employment-type
     */
    updateEmploymentType: builder.mutation({
      query: ({ userId, employment_type }) => ({
        url: `/b2b/users/${userId}/employment-type`,
        method: 'PUT',
        headers: getHeaders(),
        body: { employment_type },
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId }
      ],
    }),

    /**
     * Update user's primary branch
     * PATCH /api/v1/users/{user_id}/primary-branch
     */
    updatePrimaryBranch: builder.mutation({
      query: ({ userId, branch_id }) => ({
        url: `/b2b/users/${userId}/primary-branch`,
        method: 'PATCH',
        headers: getHeaders(),
        body: { branch_id },
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
        { type: 'UserBranches', id: userId }
      ],
    }),

    /**
     * Get all branches a user is assigned to
     * GET /api/v1/users/{user_id}/branches
     */
    getUserBranches: builder.query({
      query: (userId) => ({
        url: `/b2b/users/${userId}/branches`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, userId) => [
        { type: 'UserBranches', id: userId }
      ],
    }),

    /**
     * Update core user profile details
     * PUT /api/v1/users/{user_id}
     */
    updateUserProfile: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/b2b/users/${userId}`,
        method: 'PUT',
        headers: getHeaders(),
        body: payload,
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
      ],
    }),

    /**
     * Upload or replace a user's profile photo
     * POST /api/v1/b2b/users/{user_id}/profile-photo
     */
    uploadUserProfilePhoto: builder.mutation({
      queryFn: async ({ userId, file }) => {
        const formData = new FormData();
        formData.append('file', file, file?.name || 'profile-photo');

        try {
          const requestHeaders = getHeaders();
          // Let browser set multipart boundary automatically.
          delete requestHeaders['Content-Type'];
          delete requestHeaders['content-type'];

          // Use native fetch - bypass RTK Query entirely for file uploads
          // This ensures FormData is handled correctly by the browser
          const response = await fetch(`${api}/b2b/users/${userId}/profile-photo`, {
            method: 'POST',
            headers: requestHeaders,
            body: formData,
            credentials: 'include', // Include cookies for authentication
            // DO NOT set Content-Type - browser sets it automatically with boundary
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
            return { error: { status: response.status, data: errorData } };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          console.error('[uploadUserProfilePhoto] Fetch error:', error);
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
      ],
    }),

    /**
     * Delete a user's profile photo
     * DELETE /api/v1/b2b/users/{user_id}/profile-photo
     */
    deleteUserProfilePhoto: builder.mutation({
      query: ({ userId }) => ({
        url: `/b2b/users/${userId}/profile-photo`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
      ],
    }),

    // =============================================================================
    // ANALYTICS ENDPOINTS
    // =============================================================================

    /**
     * Get invitation analytics for the organization
     * GET /api/v1/users/invitations/analytics
     */
    getInvitationAnalytics: builder.query({
      query: ({ start_date, end_date }) => {
        const params = new URLSearchParams();

        if (start_date) params.append('start_date', start_date);
        if (end_date) params.append('end_date', end_date);

        return {
          url: `/b2b/users/invitations/analytics?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['InvitationAnalytics'],
    }),

    // =============================================================================
    // LEGACY COMPATIBILITY ENDPOINTS (Temporary)
    // =============================================================================

    /**
     * Get employee types for dropdown
     * This maps to the legacy endpoint until backend migration is complete
     */
    getEmployeeTypes: builder.query({
      queryFn: () => ({
        data: {
          employee_types: [
            { value: 'W2', label: 'W2 Employee', description: 'Traditional employee with W2 tax reporting' },
            { value: 'CONTRACTOR', label: 'Independent Contractor', description: '1099 contractor with independent status' },
          ],
        },
      }),
    }),

    /**
     * Get available roles for the organization
     */
    getAvailableRoles: builder.query({
      query: () => ({
        url: '/b2b/users/roles',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => {
        const baseRoles = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        const hasConsumer = baseRoles.some((role) => role?.value === 'consumer');
        const data = hasConsumer
          ? baseRoles
          : [...baseRoles, { value: 'consumer', label: 'Consumer' }];

        if (response && typeof response === 'object' && !Array.isArray(response)) {
          return { ...response, data };
        }
        return { data };
      },
      providesTags: ['UserRoles'],
    }),

    /**
     * Retrieve NMLS status for a user
     * GET /api/v1/users/{user_id}/nmls
     */
    getUserNmlsStatus: builder.query({
      query: (userId) => ({
        url: `/users/${userId}/nmls`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, userId) => [
        { type: 'UserNMLS', id: userId },
      ],
    }),

    /**
     * Submit or update a user's NMLS identifier
     * PUT /api/v1/users/{user_id}/nmls
     */
    updateUserNmlsId: builder.mutation({
      query: ({ userId, nmls_id }) => ({
        url: `/users/${userId}/nmls`,
        method: 'PUT',
        headers: getHeaders(),
        body: { nmls_id },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'UserDetails', id: userId },
        { type: 'UserNMLS', id: userId },
      ],
    }),

    /**
     * Verify a user's NMLS identifier with optional notes
     * POST /api/v1/users/{user_id}/nmls/verify
     */
    verifyUserNmls: builder.mutation({
      query: ({ userId, notes }) => {
        if (userId === null || userId === undefined || userId === '') {
          throw new Error('User ID is required to verify NMLS.');
        }
        return {
          url: `/users/${userId}/nmls/verify`,
          method: 'POST',
          headers: getHeaders(),
          body: { notes },
        };
      },
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
        { type: 'UserNMLS', id: userId },
      ],
    }),

    /**
     * Reject a user's NMLS identifier with notes
     * POST /api/v1/users/{user_id}/nmls/reject
     */
    rejectUserNmls: builder.mutation({
      query: ({ userId, notes }) => ({
        url: `/users/${userId}/nmls/reject`,
        method: 'POST',
        headers: getHeaders(),
        body: { notes },
      }),
      invalidatesTags: (result, error, { userId }) => [
        'Users',
        { type: 'UserDetails', id: userId },
        { type: 'UserNMLS', id: userId },
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  // User Management
  useListUsersQuery,
  useListConsumersQuery,
  useListNmlsVerifiedUsersQuery,
  useGetUserDetailsQuery,
  useInviteUserMutation,
  useInviteUserOfficerMutation,
  useInviteEmployeeMutation,
  useUpdateUserRoleMutation,
  useRemoveUserMutation,

  // Invitation Management
  useCreateInvitationMutation,
  useListInvitationsQuery,
  useRevokeInvitationMutation,
  useResendInvitationMutation,

  // User Profile & Settings
  useUpdateEmploymentTypeMutation,
  useUpdatePrimaryBranchMutation,
  useGetUserBranchesQuery,
  useUpdateUserProfileMutation,
  useUploadUserProfilePhotoMutation,
  useDeleteUserProfilePhotoMutation,

  // Analytics
  useGetInvitationAnalyticsQuery,

  // Legacy Compatibility
  useGetEmployeeTypesQuery,
  useGetAvailableRolesQuery,
  useGetUserNmlsStatusQuery,
  useUpdateUserNmlsIdMutation,
  useVerifyUserNmlsMutation,
  useRejectUserNmlsMutation,
} = userManagementV2API;
